import React, { useState } from 'react';
import { FiArrowUp, FiArrowDown, FiMessageCircle, FiHeart, FiTrash2, FiMoreVertical } from 'react-icons/fi';
import { Signal } from '../../types';
import { useApi } from '../../hooks/useApi';
import { useAuthStore } from '../../stores';
import { showToast } from '../../utils/toast';
import CommentSection from './CommentSection';
import { getProfileImageUrl, DEFAULT_AVATAR_URL } from '../../utils/imageUtils';

interface SignalCardProps {
  signal: Signal;
  onLike?: () => void;
}

const SignalCard: React.FC<SignalCardProps> = ({ signal, onLike }) => {
  const { user } = useAuthStore();
  React.useEffect(() => {
    console.log('🔍 Signal object:', signal);
    console.log('🔍 Signal author:', signal.author);
  }, [signal]);

  // Get user ID (handle both _id and id)
  const userId = React.useMemo(() => {
    return user?._id?.toString() || user?.id?.toString() || '';
  }, [user?._id, user?.id]);

  // Check if user has liked this signal (handle both string and ObjectId formats)
  const checkIsLiked = React.useCallback(() => {
    if (!userId || !signal.likes || signal.likes.length === 0) return false;
    return signal.likes.some((likeId) => {
      const likeIdStr = typeof likeId === 'string' ? likeId : String(likeId);
      return likeIdStr === userId;
    });
  }, [userId, signal.likes]);

  // Initialize state from signal prop
  const [localIsLiked, setLocalIsLiked] = React.useState(() => checkIsLiked());
  const [localLikesCount, setLocalLikesCount] = React.useState(signal.likesCount || 0);

  // Track if we're currently processing a like mutation to avoid overriding
  const isProcessingLike = React.useRef(false);

  const likeMutation = useApi.useLikeSignal({
    onSuccess: (data) => {
      console.log('✅ Like mutation success:', { isLiked: data.isLiked, likesCount: data.likesCount, likes: data.likes });

      // Mark that we're processing a like to prevent useEffect from overriding
      isProcessingLike.current = true;

      // CRITICAL: Use data.isLiked directly from API response - it's the source of truth
      setLocalIsLiked(data.isLiked);
      setLocalLikesCount(data.likesCount);

      // Also verify using likes array if provided (more reliable)
      if (data.likes && Array.isArray(data.likes) && userId) {
        const isLikedInResponse = data.likes.some((likeId: string) => String(likeId) === userId);
        console.log('🔍 Verifying like status from likes array:', { isLikedInResponse, userId, likesArray: data.likes });
        // Use the more reliable check from likes array
        setLocalIsLiked(isLikedInResponse);
      }

      // Reset the flag after a delay
      setTimeout(() => {
        isProcessingLike.current = false;
      }, 1000);

      if (onLike) onLike();
    },
    onError: (error) => {
      console.error('❌ Like mutation error:', error);
      isProcessingLike.current = false;
      // Revert optimistic update on error
      const liked = checkIsLiked();
      setLocalIsLiked(liked);
      setLocalLikesCount(signal.likesCount || 0);
    }
  });

  // Update local state when signal prop changes (on mount and when signal updates)
  React.useEffect(() => {
    // Skip sync if we just processed a like mutation (give it time to update)
    if (isProcessingLike.current) {
      setTimeout(() => {
        isProcessingLike.current = false;
      }, 500);
      return;
    }

    const liked = checkIsLiked();
    console.log('🔄 Signal prop changed, checking like status:', {
      signalId: signal._id,
      liked,
      userId,
      likesArray: signal.likes,
      likesCount: signal.likesCount
    });
    setLocalIsLiked(liked);
    setLocalLikesCount(signal.likesCount || 0);
  }, [signal._id, signal.likes, signal.likesCount, checkIsLiked, userId]);

  const isLiked = localIsLiked;
  const isSignal = signal.type === 'signal';
  const isBuy = signal.side === 'buy';

  // Check if current user is the author or admin
  const authorId = React.useMemo(() => {
    if (!signal.author) return '';
    // Handle populated object with _id
    if (typeof signal.author === 'object' && '_id' in signal.author) {
      return (signal.author as any)._id?.toString() || '';
    }
    // Handle string ID
    return String(signal.author);
  }, [signal.author]);

  const isAuthor = authorId === userId;

  const isAdmin = user?.role === 'admin';
  const canDelete = isAuthor || isAdmin;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const deleteMutation = useApi.useDeleteSignal({
    onSuccess: () => {
      const message = isSignal ? 'Signal deleted successfully' : 'Post deleted successfully';
      showToast('success', message);
      setShowDeleteConfirm(false);
      setShowMenu(false);
      // The query will be invalidated automatically, so the signal will disappear
    },
    onError: (error: any) => {
      const message = isSignal ? 'Failed to delete signal' : 'Failed to delete post';
      showToast('error', error.response?.data?.error || message);
      setShowDeleteConfirm(false);
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate(signal._id);
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Optimistic update
    const newIsLiked = !isLiked;
    const newCount = newIsLiked ? localLikesCount + 1 : Math.max(0, localLikesCount - 1);
    setLocalIsLiked(newIsLiked);
    setLocalLikesCount(newCount);
    likeMutation.mutate(signal._id);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} ${Math.floor(diffInSeconds / 60) === 1 ? 'minute' : 'minutes'} ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} ${Math.floor(diffInSeconds / 3600) === 1 ? 'hour' : 'hours'} ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} ${Math.floor(diffInSeconds / 86400) === 1 ? 'day' : 'days'} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5 mb-4 hover:shadow-md transition-shadow relative">
      {isSignal ? (
        // Signal Card
        <>
          <div className="flex items-start justify-between gap-2 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700">
                <img
                  src={getProfileImageUrl((signal.author as any)?.profileImage)}
                  alt={(signal.author as any)?.name}
                  crossOrigin="anonymous"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = DEFAULT_AVATAR_URL;
                  }}
                />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-gray-900 dark:text-white break-anywhere">
                  {(signal.author as any)?.name || 'Unknown Master'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatTimeAgo(signal.createdAt)}
                </div>
              </div>
            </div>
            <span
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold ${isBuy
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}
            >
              {signal.side?.toUpperCase()}
            </span>
          </div>

          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <span
                className={`flex-shrink-0 px-3 py-1 rounded-full text-sm font-bold ${isBuy
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}
              >
                {signal.side?.toUpperCase()}
              </span>
              <div className="min-w-0">
                <div className="font-bold text-base sm:text-lg text-gray-900 dark:text-white break-anywhere">
                  {signal.symbol} @ ${signal.price?.toFixed(2)}
                </div>
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              {canDelete && (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    type="button"
                  >
                    <FiMoreVertical className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(true);
                          setShowMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-lg"
                        type="button"
                      >
                        <FiTrash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {(signal.target || signal.stopLoss) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-sm">
              {signal.target && (
                <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <FiArrowUp className="h-4 w-4" />
                  <span className="font-semibold">Target: ${signal.target.toFixed(2)}</span>
                </div>
              )}
              {signal.stopLoss && (
                <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                  <FiArrowDown className="h-4 w-4" />
                  <span className="font-semibold">Stop Loss: ${signal.stopLoss.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          <p className="text-gray-700 dark:text-gray-300 mb-3 break-words">{signal.description}</p>

          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${isLiked
                ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              <FiHeart className={`h-4 w-4 ${isLiked ? 'fill-current text-red-600' : ''}`} />
              <span className="text-sm font-semibold">{localLikesCount}</span>
            </button>
          </div>

          <CommentSection signalId={signal._id} signalAuthorId={authorId} />
        </>
      ) : (
        // Post Card
        <>
          <div className="flex items-start justify-between gap-2 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700">
                <img
                  src={getProfileImageUrl((signal.author as any)?.profileImage)}
                  alt={(signal.author as any)?.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = DEFAULT_AVATAR_URL;
                  }}
                />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-gray-900 dark:text-white break-anywhere">
                  {(signal.author as any)?.name || 'Unknown Master'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatTimeAgo(signal.createdAt)}
                </div>
              </div>
            </div>
            <span className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center gap-1">
              <FiMessageCircle className="h-3 w-3" />
              Post
            </span>
          </div>

          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center gap-1">
                <FiMessageCircle className="h-3 w-3" />
                Post
              </span>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              {canDelete && (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    type="button"
                  >
                    <FiMoreVertical className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(true);
                          setShowMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-lg"
                        type="button"
                      >
                        <FiTrash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <p className="text-gray-700 dark:text-gray-300 mb-3 break-words">{signal.description}</p>

          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${isLiked
                ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              <FiHeart className={`h-4 w-4 ${isLiked ? 'fill-current text-red-600' : ''}`} />
              <span className="text-sm font-semibold">{localLikesCount} likes</span>
            </button>
          </div>

          <CommentSection signalId={signal._id} signalAuthorId={authorId} />
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Delete {isSignal ? 'Signal' : 'Post'}?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this {isSignal ? 'signal' : 'post'}? This action cannot be undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="w-full sm:w-auto px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignalCard;

